import React from 'react'
import {Banner} from '@primer/react/experimental'
import {ShieldLockIcon} from '@primer/octicons-react'
import {useLocation} from '@github-ui/react-router'
import {deleteCookie, getCookie, setCookie} from '@github-ui/cookies'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useSso} from '@github-ui/use-sso'
import {sendStats} from '@github-ui/stats'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import styles from './GlobalSSOBanner.module.css'
import {SlideDismiss} from './components/SlideDismiss'
import {ConfirmationDialog} from './components/dialog/ConfirmationDialog'
import {SSOActionMenu} from './components/SSOActionMenu'
import {DEFAULT_COOKIE_EXPIRATION_DAYS} from './components/dialog/constants'
import {MAX_DISPLAYED_ORGS, COOKIE_NAME, ANALYTICS_CATEGORY} from './constants'
import type {SsoOrganization} from './types'

function storeDismissedOrgIds(ssoOrgs: GlobalSsoBannerProps['ssoOrgs'], durationDays?: number | null) {
  const orgIds = ssoOrgs.map(org => org.id).sort()

  deleteCookie(COOKIE_NAME) // clear the old cookie before setting a new one

  if (durationDays === null) {
    // Indefinite duration - actually just sets a very distant date (10 years)
    const expiration = new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toUTCString()
    setCookie(COOKIE_NAME, JSON.stringify(orgIds), expiration)
  } else {
    // Use the specified duration or fall back to the default
    const days = durationDays ?? DEFAULT_COOKIE_EXPIRATION_DAYS
    const expirationTime = days * 24 * 60 * 60 * 1000 // expiration days in milliseconds
    const expiration = new Date(Date.now() + expirationTime).toUTCString()
    setCookie(COOKIE_NAME, JSON.stringify(orgIds), expiration)
  }
}

/** Gets all the orgs from the last time the banner was dismissed via the stored cookie */
function getDismissedOrgIdsFromCookie(): string[] {
  const cookie = getCookie(COOKIE_NAME)

  if (cookie?.value) {
    try {
      const orgIds = JSON.parse(cookie.value)
      return Array.isArray(orgIds) ? orgIds : []
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (_error: unknown) {
      // If parsing fails, we assume the cookie is invalid and delete it
      deleteCookie(COOKIE_NAME)
      return []
    }
  }

  return []
}

/**
 * Check if the SSO banner info stored as a cookie matches what is currently tried to be displayed.
 * If it does, the banner will be suppressed.
 */
function shouldCookieSuppressBanner(ssoOrgs: GlobalSsoBannerProps['ssoOrgs']): boolean {
  const dismissedOrgIds = getDismissedOrgIdsFromCookie()
  return ssoOrgs.length === dismissedOrgIds.length && ssoOrgs.every(org => dismissedOrgIds.includes(org.id))
}

export interface GlobalSsoBannerProps {
  ssoOrgs: SsoOrganization[]
}

export function isSSOOrgInfo(ssoOrg: unknown): ssoOrg is SsoOrganization {
  return typeof ssoOrg === 'object' && ssoOrg !== null && 'id' in ssoOrg && 'name' in ssoOrg && 'login' in ssoOrg
}

export function GlobalSsoBanner({ssoOrgs}: GlobalSsoBannerProps) {
  const location = useLocation()
  const [bannerState, setBannerState] = React.useState<'visible' | 'hidden'>('visible')
  const [dialogIsOpen, setDialogIsOpen] = React.useState(false)
  const hasSentMetricRef = React.useRef(false)
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const dialogEnabled = isFeatureEnabled('global_sso_banner_dismiss_dialog')

  React.useEffect(() => {
    if (!hasSentMetricRef.current && ssoOrgs.length > 0) {
      if (isFeatureEnabled('record_sso_banner_metrics')) {
        sendStats({
          incrementKey: 'GLOBAL_SSO_BANNER_DISPLAYED',
          requestUrl: window.location.href,
        })
      }
      hasSentMetricRef.current = true
    }
  }, [ssoOrgs])

  if (ssoOrgs.length === 0) {
    return null
  }

  const dismissSsoOrganizations = (durationDays: number | null = DEFAULT_COOKIE_EXPIRATION_DAYS) => {
    setBannerState('hidden')
    storeDismissedOrgIds(ssoOrgs, durationDays)
  }

  // Hide the banner when the user clicks the close button
  const handleDismiss = () => {
    sendClickAnalyticsEvent({
      category: ANALYTICS_CATEGORY,
      action: 'click_dismiss_button',
      label: 'banner_dismissed',
    })

    if (dialogEnabled) {
      setDialogIsOpen(true)
    } else {
      dismissSsoOrganizations()
    }
  }

  const handleDialogDismiss = (durationDays: number | null) => {
    dismissSsoOrganizations(durationDays)
    setDialogIsOpen(false)
  }

  const renderDescription = () => {
    if (ssoOrgs.length === 0) return 'Single sign-on to see results'

    const displayOrgs = ssoOrgs.slice(0, MAX_DISPLAYED_ORGS)
    const remainingCount = ssoOrgs.length - MAX_DISPLAYED_ORGS
    const organizationText = ssoOrgs.length === 1 ? 'organization' : 'organizations'

    const formatOrgList = () => {
      const formattedOrgNames = displayOrgs.map(org => <strong key={org.login}>{org.name}</strong>)

      if (formattedOrgNames.length === 1) {
        return formattedOrgNames[0]
      } else if (formattedOrgNames.length === 2) {
        return (
          <>
            {formattedOrgNames[0]} and {formattedOrgNames[1]}
          </>
        )
      } else {
        // MAX_DISPLAYED_ORGS orgs, with potential overflow
        const lastIndex = formattedOrgNames.length - 1
        const baseList = (
          <>
            {formattedOrgNames[0]}, {formattedOrgNames[1]}, and {formattedOrgNames[lastIndex]}
          </>
        )
        return remainingCount > 0 ? (
          <>
            {baseList}, and {remainingCount} more
          </>
        ) : (
          baseList
        )
      }
    }

    return (
      <>
        Single sign-on to see results in the {formatOrgList()} {organizationText}
      </>
    )
  }

  return (
    <>
      <SlideDismiss hidden={bannerState !== 'visible'}>
        <Banner
          title="Single-sign on"
          className={styles.banner}
          hideTitle
          icon={<ShieldLockIcon />}
          description={renderDescription()}
          onDismiss={handleDismiss}
          primaryAction={<SSOActionMenu ssoOrgs={ssoOrgs} redirectLocation={location} />}
          data-testid="global-sso-banner"
        />
      </SlideDismiss>

      <ConfirmationDialog
        isOpen={dialogEnabled && dialogIsOpen}
        onClose={() => setDialogIsOpen(false)}
        onConfirm={handleDialogDismiss}
        ssoOrgs={ssoOrgs}
      />
    </>
  )
}

type GlobalSSOBannerAppPayload = {
  current_sso_orgs_match_dismissed_cookie?: boolean
}

function useGlobalBannerSSO() {
  const {ssoOrgs, baseAvatarUrl} = useSso()
  const {current_sso_orgs_match_dismissed_cookie} = useAppPayload<GlobalSSOBannerAppPayload>() ?? {}

  const ssoOrgsWithAvatars = React.useMemo(
    () =>
      ssoOrgs.filter(isSSOOrgInfo).map(
        org =>
          ({
            ...org,
            avatar_url: `${baseAvatarUrl}/u/${org.id}`,
          }) as SsoOrganization,
      ),
    [ssoOrgs, baseAvatarUrl],
  )

  return {
    suppressBanner: current_sso_orgs_match_dismissed_cookie === true || shouldCookieSuppressBanner(ssoOrgsWithAvatars),
    ssoOrgs: ssoOrgsWithAvatars,
    baseAvatarUrl,
  }
}

/** Entry point for the GlobalSSOBanner when used in React pages */
export function ContextualGlobalSsoBanner() {
  const {ssoOrgs, suppressBanner} = useGlobalBannerSSO()

  if (ssoOrgs.length === 0 || !isFeatureEnabled('global_sso_banner') || suppressBanner) {
    return null
  }

  return <GlobalSsoBanner ssoOrgs={ssoOrgs} />
}

export const SSO_BANNER_COOKIE_NAME = COOKIE_NAME

try{ GlobalSsoBanner.displayName ||= 'GlobalSsoBanner' } catch {}
try{ ContextualGlobalSsoBanner.displayName ||= 'ContextualGlobalSsoBanner' } catch {}